import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Calendar, MapPin, Mail, Phone, FileText, Link as LinkIcon, Film, Image as ImageIcon, MessageSquare, CheckCircle2, Download, Share2, Radio, Trash2, Clock, Pencil, Sparkles, Upload } from "lucide-react";
import api from "@/lib/api";
import { useTranslation } from "react-i18next";
import StatusBadge from "@/components/StatusBadge";
import CommentablePreview from "@/components/CommentablePreview";
import AdditionalLinksPanel from "@/components/AdditionalLinksPanel";
import ScriptEditor from "@/components/ScriptEditor";
import AssetComments from "@/components/AssetComments";
import { downloadStatic, downloadAuthed } from "@/lib/download";
import PhotoGallery from "@/components/PhotoGallery";
import LocationWeatherButton from "@/components/LocationWeatherButton";
import ShareLinkDialog from "@/components/ShareLinkDialog";
import EditRequestDetailsModal from "@/components/EditRequestDetailsModal";
import MMApprovalPanel from "@/components/MMApprovalPanel";
import ZipDownloadOverlay, { useZipDownload, useFolderDownload, FolderDownloadOverlay } from "@/components/ZipDownloadOverlay";
import { buildProjectAssetList, buildAgentPhotoList } from "@/lib/projectAssets";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import AgentPhotoToolsModal from "@/components/AgentPhotoToolsModal";
import PhotographerPhotoLightbox from "@/components/PhotographerPhotoLightbox";
import { CreditPurchaseReturnWatcher } from "@/components/CreditsTopUpModal";
import { toast } from "sonner";

// DD/MM/YYYY formatter
const fmtDate = (d) => {
    if (!d) return "";
    const dt = typeof d === "string" ? new Date(d.length <= 10 ? `${d}T00:00:00` : d) : d;
    if (isNaN(dt)) return d;
    return dt.toLocaleDateString("nl-BE", { day: "2-digit", month: "2-digit", year: "numeric" });
};

export default function ProjectDetail() {
    const { id } = useParams();
    const { user } = useAuth();
    const isPhotographer = user?.account_type === "photographer";
    const photogFileRef = React.useRef(null);
    const [photogUploading, setPhotogUploading] = useState(false);
    const [photogLightboxIdx, setPhotogLightboxIdx] = useState(null);
    const [photogDragIdx, setPhotogDragIdx] = useState(null);
    const [photogOverIdx, setPhotogOverIdx] = useState(null);
    const [photogReordering, setPhotogReordering] = useState(false);
    const [photogCommentCounts, setPhotogCommentCounts] = useState({});

    const handlePhotogReorder = async (targetIdx) => {
        const from = photogDragIdx;
        setPhotogDragIdx(null);
        setPhotogOverIdx(null);
        if (from === null || from === targetIdx || photogReordering) return;
        const arr = [...(req?.ai_photo_urls || [])];
        const [moved] = arr.splice(from, 1);
        arr.splice(targetIdx, 0, moved);
        setPhotogReordering(true);
        try {
            await api.post(`/requests/${id}/ai-photos/reorder`, { urls: arr });
            toast.success("Volgorde aangepast");
            refetchProject();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Herordenen mislukt");
        }
        setPhotogReordering(false);
    };

    const handlePhotogUpload = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        setPhotogUploading(true);
        try {
            const urls = [];
            for (const f of files) {
                const fd = new FormData();
                fd.append("file", f);
                const { data } = await api.post("/upload", fd);
                urls.push(data.url);
            }
            await api.post(`/requests/${id}/fotoreeks-photos`, { urls });
            toast.success(`${urls.length} foto('s) toegevoegd aan de fotoreeks`);
            refetchProject();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Upload mislukt");
        }
        setPhotogUploading(false);
        if (photogFileRef.current) photogFileRef.current.value = "";
    };

    const handlePhotogDelete = async (url) => {
        try {
            await api.patch(`/requests/${id}/media/delete`, { ai_photo_urls: [url] });
            toast.success("Foto verwijderd");
            refetchProject();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Verwijderen mislukt");
        }
    };
    const { t } = useTranslation();
    const [req, setReq] = useState(null);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [approving, setApproving] = useState(false);
    const [approvingReel, setApprovingReel] = useState(false);
    const [downloadOffer, setDownloadOffer] = useState(null); // {url, name, label} | null after approve
    const [liveLoading, setLiveLoading] = useState(false);
    const [shareDialog, setShareDialog] = useState(null); // {url, title, subject} | null
    const [editDetailsOpen, setEditDetailsOpen] = useState(false);
    const [editingDescription, setEditingDescription] = useState(false);
    const [descriptionDraft, setDescriptionDraft] = useState("");
    const [savingDescription, setSavingDescription] = useState(false);
    const zipDl = useZipDownload();
    const folderDl = useFolderDownload();
    const [aiToolsOpen, setAiToolsOpen] = useState(false);
    const [aiToolsIdx, setAiToolsIdx] = useState(null);

    useEffect(() => {
        if (!isPhotographer || !id) return;
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get(`/requests/${id}/comments`);
                if (cancelled) return;
                const counts = {};
                (data || []).forEach((c) => {
                    if (c.asset_type === "ai_photos" && c.photo_index !== null && c.photo_index !== undefined) {
                        counts[c.photo_index] = (counts[c.photo_index] || 0) + 1;
                    }
                });
                setPhotogCommentCounts(counts);
            } catch { /* non-blocking */ }
        })();
        return () => { cancelled = true; };
    }, [isPhotographer, id, req?.updated_at]);

    // Listen for per-photo "Bewerk met AI" clicks coming from the embedded fotoreeks iframe
    useEffect(() => {
        const onMsg = (e) => {
            if (e.data?.type === "sf-edit-photo" && typeof e.data.index === "number") {
                setAiToolsIdx(e.data.index);
                setAiToolsOpen(true);
            }
        };
        window.addEventListener("message", onMsg);
        return () => window.removeEventListener("message", onMsg);
    }, []);

    // Makelaars / MMs with at least one tool activated can use credits to
    // edit their delivered photos in-place. Admins use the dedicated /tools.
    const hasAnyTool = !!user && user.role !== "admin" && Object.values(user.tool_access || {}).some(Boolean);

    const refetchProject = async () => {
        try {
            const { data } = await api.get(`/requests/${id}`);
            setReq(data);
        } catch (e) { /* noop */ }
    };

    const startEditDescription = () => {
        setDescriptionDraft(req?.description || "");
        setEditingDescription(true);
    };

    const saveDescription = async () => {
        setSavingDescription(true);
        try {
            const { data } = await api.patch(`/requests/${id}/description`, { description: descriptionDraft });
            setReq(data);
            setEditingDescription(false);
            toast.success(t("pd.desc_saved"));
        } catch (e) {
            toast.error(e.response?.data?.detail || t("pd.save_failed"));
        }
        setSavingDescription(false);
    };

    const downloadAllPhotos = () => {
        const hasAny = (req?.photo_urls?.length || 0) + (req?.ai_photo_urls?.length || 0) > 0;
        if (!hasAny) return;
        const safe = (req.address_street || "fotos").replace(/[^a-z0-9]/gi, "_");
        folderDl.start({
            folderName: `${safe}_fotos`,
            files: buildAgentPhotoList(req, safe),
        });
    };

    const trackDownload = (asset_type) => {
        // Fire-and-forget — don't block the actual download
        api.post(`/requests/${id}/track-download`, { asset_type }).then((r) => setReq(r.data)).catch(() => {});
    };

    const toggleLive = async () => {
        setLiveLoading(true);
        try {
            const { data } = await api.patch(`/requests/${id}/live`, { is_live: !req.is_live });
            setReq(data);
            toast.success(data.is_live ? t("pd.live_on") : t("pd.live_off"));
        } catch (e) {
            toast.error(e.response?.data?.detail || t("pd.err"));
        }
        setLiveLoading(false);
    };

    useEffect(() => {
        Promise.all([
            api.get(`/requests/${id}`).catch((e) => {
                if (e.response?.status === 404 || e.response?.status === 403) return { data: null };
                throw e;
            }),
            api.get("/products", { params: { include_hidden: true } }),
        ])
            .then(([a, b]) => { setReq(a.data); setProducts(b.data); })
            .finally(() => setLoading(false));
    }, [id]);

    // Scroll to top when navigating to a different project
    useEffect(() => { window.scrollTo({ top: 0, left: 0, behavior: "auto" }); }, [id]);

    const approvePreview = async () => {
        setApproving(true);
        try {
            const { data } = await api.post(`/requests/${id}/approve-preview`);
            setReq(data);
            toast.success(t("pd.approved_toast"));
            if (data.preview_video_url) {
                setDownloadOffer({
                    url: data.preview_video_url,
                    name: `preview_${(data.address_street || "video").replace(/\s+/g, "_")}.mp4`,
                    label: "Preview video",
                });
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || t("pd.err"));
        }
        setApproving(false);
    };

    const approveReelPreview = async () => {
        setApprovingReel(true);
        try {
            const { data } = await api.post(`/requests/${id}/approve-reel-preview`);
            setReq(data);
            toast.success(t("pd.reel_approved_toast"));
            if (data.reel_preview_url) {
                setDownloadOffer({
                    url: data.reel_preview_url,
                    name: `reel_preview_${(data.address_street || "video").replace(/\s+/g, "_")}.mp4`,
                    label: "Reel preview",
                });
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || t("pd.err"));
        }
        setApprovingReel(false);
    };

    if (loading) return <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center text-zinc-500">{t("pd.loading")}</div>;
    if (!req) return <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center text-zinc-500">{t("pd.not_found")}</div>;

    const BACKEND = process.env.REACT_APP_BACKEND_URL;

    const reload = async () => {
        const { data } = await api.get(`/requests/${id}`);
        setReq(data);
    };

    const copyShareLink = (url) => {
        const full = url.startsWith("http") ? url : `${BACKEND}${url}`;
        navigator.clipboard?.writeText(full);
        toast.success(t("pd.copied"));
    };

    const openShare = (url, label) => {
        const addr = req.address_street || "video";
        setShareDialog({
            url,
            title: `${t("pd.share_prefix")} ${label}`,
            subject: `${label} — ${addr}`,
        });
    };

    return (
        <div className="min-h-screen bg-[#FAFAFA] text-zinc-950 font-body pb-20" data-testid="project-detail-page">
            <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-10">
                <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-950 mb-6" data-testid="back-to-dashboard">
                    <ArrowLeft className="h-4 w-4" /> {t("pd.back")}
                </Link>
                {/* Alles downloaden — server bouwt één ZIP met alle video's, reels en foto's */}
                {(req.final_video_url || req.reel_url || (req.ai_photo_urls?.length > 0) || (req.photo_urls?.length > 0) || (req.script?.length > 0)) && (
                    <div className="flex justify-end mb-2">
                        <button
                            onClick={() => {
                                const safe = (req.address_street || req.id).replace(/[^\w-]/g, "_");
                                folderDl.start({
                                    folderName: safe,
                                    files: buildProjectAssetList(req, safe),
                                });
                            }}
                            disabled={folderDl.state.active}
                            className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 border border-emerald-500/30 text-emerald-600 hover:bg-emerald-500/10 rounded-sm transition active:scale-95 disabled:opacity-50 disabled:pointer-events-none"
                            data-testid="download-project-zip-btn"
                        >
                            <Download className="h-3.5 w-3.5" /> {t("pd.download_all")}
                        </button>
                    </div>
                )}

                <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10">
                    <div>
                        <div className="flex flex-wrap items-center gap-3 mb-4">
                            <StatusBadge status={req.status} />
                            {req.is_live && (
                                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-sm bg-[#EF4444]/20 border border-[#EF4444]/40 text-[#EF4444]" data-testid="live-badge">
                                    <Radio className="h-2.5 w-2.5 animate-pulse" /> Live
                                </span>
                            )}
                            <span className="text-xs text-zinc-500">
                                {t("pd.created")} {fmtDate(req.created_at)}
                            </span>
                        </div>
                        <h1 className="font-display font-black text-4xl lg:text-5xl tracking-tighter leading-none">
                            {req.address_street}
                        </h1>
                        <p className="text-zinc-500 mt-2">{req.address_zip} {req.address_city}</p>
                        <div className="mt-3 flex items-center gap-2 flex-wrap">
                            <LocationWeatherButton address={`${req.address_street}, ${req.address_zip || ""} ${req.address_city}`.trim()} />
                            <button
                                type="button"
                                onClick={() => setEditDetailsOpen(true)}
                                className="inline-flex items-center gap-2 h-8 px-3 rounded-sm text-xs font-medium border border-zinc-200 bg-[#FFFFFF] text-zinc-600 hover:text-zinc-950 hover:border-[#E5A93D]/40 hover:bg-[#E5A93D]/5 transition active:scale-[0.98]"
                                data-testid="edit-details-btn"
                            >
                                <Pencil className="h-3.5 w-3.5 text-[#E5A93D]" />
                                {t("pd.edit_request")}
                            </button>
                        </div>
                        {(req.co_agents?.length > 0) && (
                            <div className="mt-4 flex items-center gap-2 flex-wrap" data-testid="co-agents-strip">
                                <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold">{t("pd.also_on")}</span>
                                {req.co_agents.map((a) => (
                                    <span
                                        key={a.id}
                                        className="inline-flex items-center gap-2 h-7 pl-1 pr-2 bg-[#F4F4F5] border border-zinc-200 rounded-sm text-[11px] text-zinc-800"
                                        title={a.email}
                                    >
                                        {a.logo_url ? (
                                            <img src={a.logo_url.startsWith("http") ? a.logo_url : `${process.env.REACT_APP_BACKEND_URL}${a.logo_url}`} alt="" className="h-5 w-5 rounded-sm object-contain bg-zinc-100" />
                                        ) : (
                                            <div className="h-5 w-5 rounded-sm bg-[#E5A93D]/15 text-[#E5A93D] flex items-center justify-center text-[9px] font-bold">
                                                {(a.name || a.email || "?").slice(0, 1).toUpperCase()}
                                            </div>
                                        )}
                                        {a.name || a.email}
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                    <div className="flex flex-col items-end gap-3">
                        {!isPhotographer && (
                            <div className="text-right">
                                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{t("pd.total")}</div>
                                <div className="font-display font-black text-4xl text-[#E5A93D]">€{req.total_price}</div>
                            </div>
                        )}
                        {(req.final_video_url || req.reel_url) && (
                            <button
                                onClick={toggleLive}
                                disabled={liveLoading}
                                className={`inline-flex items-center gap-2 h-10 px-4 rounded-sm text-sm font-bold transition active:scale-[0.98] ${req.is_live ? "bg-[#EF4444]/15 border border-[#EF4444]/40 text-[#EF4444] hover:bg-[#EF4444]/25" : "bg-zinc-100 border border-zinc-200 text-zinc-600 hover:bg-zinc-100 hover:text-zinc-950"}`}
                                data-testid="toggle-live-btn"
                            >
                                <Radio className={`h-4 w-4 ${req.is_live ? "animate-pulse" : ""}`} />
                                {req.is_live ? t("pd.is_live") : t("pd.set_live")}
                            </button>
                        )}
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Deliverables (main) */}
                    <div className="lg:col-span-2 space-y-6">
                        {!isPhotographer && <MMApprovalPanel req={req} currentUser={user} onUpdated={(updated) => setReq(updated)} />}
                        {(req.final_video_url || req.reel_url) && (
                            <div className="bg-[#FFFFFF] border border-zinc-100 rounded-md p-6 lg:p-8" data-testid="deliverables-section">
                                <div className="flex items-center gap-2 mb-6">
                                    <Film className="h-4 w-4 text-[#E5A93D]" />
                                    <h2 className="font-display font-bold text-xl tracking-tight">{t("pd.delivered_media")}</h2>
                                </div>
                                {req.final_video_url && (
                                    <div className="mb-6">
                                        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                                            <div className="text-xs uppercase tracking-wider text-zinc-500">{t("pd.final_video")}</div>
                                            <div className="flex items-center gap-2">
                                                {!req.final_video_url.startsWith("http") && (
                                                    <button
                                                        type="button"
                                                        onClick={() => { trackDownload("final"); downloadStatic(req.final_video_url, `finale_${(req.address_street||req.id).replace(/[^a-z0-9]/gi,"_")}.mp4`); }}
                                                        className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 bg-[#E5A93D] hover:bg-[#C8922C] text-black font-bold rounded-sm"
                                                        data-testid="download-final-video"
                                                    >
                                                        <Download className="h-3.5 w-3.5" /> Download
                                                    </button>
                                                )}
                                                <button onClick={() => openShare(req.final_video_url, t("pd.final_video").toLowerCase())} className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 bg-zinc-100 hover:bg-zinc-100 border border-zinc-200 text-zinc-950 rounded-sm" data-testid="share-final-video">
                                                    <Share2 className="h-3.5 w-3.5" /> {t("pd.share_link")}
                                                </button>
                                            </div>
                                        </div>
                                        <RetentionInfo downloadedAt={req.final_downloaded_at} deletionAt={req.final_deletion_scheduled_at} />
                                        <CommentablePreview videoUrl={req.final_video_url} requestId={req.id} currentUser={user} assetType="final" />
                                    </div>
                                )}
                                {req.reel_url && (
                                    <div>
                                        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                                            <div className="text-xs uppercase tracking-wider text-zinc-500">{t("pd.reel")}</div>
                                            <div className="flex items-center gap-2">
                                                {!req.reel_url.startsWith("http") && (
                                                    <button
                                                        type="button"
                                                        onClick={() => { trackDownload("reel"); downloadStatic(req.reel_url, `reel_${(req.address_street||req.id).replace(/[^a-z0-9]/gi,"_")}.mp4`); }}
                                                        className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 bg-[#E5A93D] hover:bg-[#C8922C] text-black font-bold rounded-sm"
                                                        data-testid="download-reel"
                                                    >
                                                        <Download className="h-3.5 w-3.5" /> Download
                                                    </button>
                                                )}
                                                <button onClick={() => openShare(req.reel_url, "reel")} className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 bg-zinc-100 hover:bg-zinc-100 border border-zinc-200 text-zinc-950 rounded-sm" data-testid="share-reel">
                                                    <Share2 className="h-3.5 w-3.5" /> {t("pd.share_link")}
                                                </button>
                                            </div>
                                        </div>
                                        <RetentionInfo downloadedAt={req.reel_downloaded_at} deletionAt={req.reel_deletion_scheduled_at} />
                                        <CommentablePreview videoUrl={req.reel_url} requestId={req.id} currentUser={user} assetType="reel" />
                                    </div>
                                )}
                            </div>
                        )}

                        {req.preview_video_url && (
                            <div className={`bg-[#FFFFFF] border rounded-md p-6 lg:p-8 ${req.preview_approved ? "border-emerald-500/40" : "border-[#E5A93D]/30"}`} data-testid="preview-section">
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-2">
                                    <div className="flex items-center gap-2">
                                        <MessageSquare className="h-4 w-4 text-[#E5A93D]" />
                                        <h2 className="font-display font-bold text-xl tracking-tight">{t("pd.preview_approval")}</h2>
                                    </div>
                                    {req.preview_approved ? (
                                        <div className="flex items-center gap-2 text-emerald-600 text-sm font-medium" data-testid="preview-approved-badge">
                                            <CheckCircle2 className="h-4 w-4" />
                                            {t("pd.approved_on", { date: fmtDate(req.preview_approved_at) })}
                                        </div>
                                    ) : (
                                        <Button
                                            onClick={approvePreview}
                                            disabled={approving}
                                            className="bg-emerald-500 hover:bg-emerald-400 text-black font-bold rounded-sm active:scale-[0.98]"
                                            data-testid="approve-preview-btn"
                                        >
                                            <CheckCircle2 className="mr-2 h-4 w-4" />
                                            {approving ? t("pd.busy") : t("pd.approve")}
                                        </Button>
                                    )}
                                </div>
                                <p className="text-sm text-zinc-500 mb-6">
                                    {req.preview_approved
                                        ? t("pd.preview_approved_body")
                                        : t("pd.preview_hint")
                                    }
                                </p>
                                <CommentablePreview
                                    videoUrl={req.preview_video_url}
                                    requestId={req.id}
                                    currentUser={user}
                                />
                            </div>
                        )}

                        {req.reel_preview_url && (
                            <div className={`bg-[#FFFFFF] border rounded-md p-6 lg:p-8 ${req.reel_preview_approved ? "border-emerald-500/40" : "border-[#E5A93D]/30"}`} data-testid="reel-preview-section">
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-2">
                                    <div className="flex items-center gap-2">
                                        <MessageSquare className="h-4 w-4 text-[#E5A93D]" />
                                        <h2 className="font-display font-bold text-xl tracking-tight">{t("pd.reel_preview_approval")}</h2>
                                    </div>
                                    {req.reel_preview_approved ? (
                                        <div className="flex items-center gap-2 text-emerald-600 text-sm font-medium" data-testid="reel-preview-approved-badge">
                                            <CheckCircle2 className="h-4 w-4" />
                                            {t("pd.approved_on", { date: fmtDate(req.reel_preview_approved_at) })}
                                        </div>
                                    ) : (
                                        <Button
                                            onClick={approveReelPreview}
                                            disabled={approvingReel}
                                            className="bg-emerald-500 hover:bg-emerald-400 text-black font-bold rounded-sm active:scale-[0.98]"
                                            data-testid="approve-reel-preview-btn"
                                        >
                                            <CheckCircle2 className="mr-2 h-4 w-4" />
                                            {approvingReel ? t("pd.busy") : t("pd.approve_reel")}
                                        </Button>
                                    )}
                                </div>
                                <p className="text-sm text-zinc-500 mb-6">
                                    {req.reel_preview_approved
                                        ? t("pd.reel_approved_body")
                                        : t("pd.reel_hint")
                                    }
                                </p>
                                <CommentablePreview
                                    videoUrl={req.reel_preview_url}
                                    requestId={req.id}
                                    currentUser={user}
                                    assetType="reel_preview"
                                />
                            </div>
                        )}

                        {!isPhotographer && req.ai_photo_urls && req.ai_photo_urls.length > 0 && (
                            <div className="bg-[#FFFFFF] border border-zinc-100 rounded-md p-6 lg:p-8" data-testid="ai-photos-section">
                                <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
                                    <div className="flex items-center gap-2">
                                        <ImageIcon className="h-4 w-4 text-[#E5A93D]" />
                                        <h2 className="font-display font-bold text-xl tracking-tight">{t("pd.photos_title")}</h2>
                                    </div>
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <Button
                                            onClick={async () => {
                                                try {
                                                    const { data } = await api.get(`/requests/${req.id}/photos-share-token`);
                                                    openShare(`/preview/${data.token}`, t("pd.photos_title").toLowerCase());
                                                } catch (e) {
                                                    toast.error(t("pd.share_fail"));
                                                }
                                            }}
                                            data-testid="share-photos-btn"
                                            variant="ghost"
                                            className="h-9 px-3 rounded-sm bg-zinc-100 hover:bg-zinc-100 border border-zinc-200 text-zinc-950 inline-flex items-center gap-2"
                                            title="Deel de fotoreeks met een publieke link"
                                        >
                                            <Share2 className="h-3.5 w-3.5" />
                                            {t("pd.share")}
                                        </Button>
                                        {hasAnyTool && (
                                            <Button
                                                onClick={() => setAiToolsOpen(true)}
                                                data-testid="open-ai-tools-modal-btn"
                                                className="bg-[#E5A93D] hover:bg-[#C8922C] text-black font-bold rounded-sm h-9 px-4 active:scale-[0.98] inline-flex items-center gap-2"
                                                title="Bewerk je foto's met AI — credits worden afgeboekt"
                                            >
                                                <Sparkles className="h-4 w-4" />
                                                {t("pd.edit_ai")}
                                                <span className="ml-1 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider bg-black/20 text-black/80 px-1.5 py-0.5 rounded-sm">
                                                    {user?.credits_balance ?? 0} {t("pd.credits")}
                                                </span>
                                            </Button>
                                        )}
                                    </div>
                                </div>
                                <PhotosPreviewEmbed requestId={req.id} editable={hasAnyTool} key={`ph-${req.ai_photo_urls.length}-${(req.updated_at || "").slice(0, 19)}`} />
                            </div>
                        )}

                        {/* Fotograaf: fotoreeks uploaden & beheren */}
                        {isPhotographer && (
                            <div className="bg-[#FFFFFF] border border-zinc-100 rounded-md p-6 lg:p-8" data-testid="photographer-upload-section">
                                <div className="flex items-center gap-2 mb-2">
                                    <Upload className="h-4 w-4 text-[#0EA5E9]" />
                                    <h2 className="font-display font-bold text-xl tracking-tight">Fotoreeks beheren</h2>
                                </div>
                                <p className="text-sm text-zinc-500 mb-4">Upload hier de foto's van je shoot — ze verschijnen meteen in de fotoreeks van dit project.</p>
                                <input ref={photogFileRef} type="file" accept="image/*" multiple className="hidden" onChange={handlePhotogUpload} data-testid="photographer-file-input" />
                                <button
                                    type="button"
                                    onClick={() => photogFileRef.current?.click()}
                                    disabled={photogUploading}
                                    className="inline-flex items-center gap-2 h-10 px-4 bg-[#E5A93D] hover:bg-[#C8922C] text-black text-sm font-bold rounded-sm transition active:scale-95 disabled:opacity-60"
                                    data-testid="photographer-upload-btn"
                                >
                                    <Upload className="h-4 w-4" /> {photogUploading ? "Bezig met uploaden…" : "Foto's uploaden"}
                                </button>
                                {(req.ai_photo_urls || []).length > 0 && (
                                    <>
                                        {(req.ai_photo_urls || []).length > 1 && (
                                            <p className="text-[11px] text-zinc-400 mt-5 mb-2" data-testid="photographer-reorder-hint">
                                                Versleep foto's om de volgorde aan te passen — de beste eerst.
                                            </p>
                                        )}
                                        <div className={`grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 ${(req.ai_photo_urls || []).length > 1 ? "" : "mt-5"}`}>
                                        {req.ai_photo_urls.map((u, i) => (
                                            <div
                                                key={`${u}-${i}`}
                                                className={`relative group aspect-square overflow-hidden rounded-sm border transition ${photogDragIdx === i ? "opacity-40 border-zinc-200" : photogOverIdx === i && photogDragIdx !== null ? "border-[#E5A93D] ring-2 ring-[#E5A93D]/50" : "border-zinc-200"} ${photogReordering ? "pointer-events-none opacity-60" : ""}`}
                                                draggable={(req.ai_photo_urls || []).length > 1}
                                                onDragStart={() => setPhotogDragIdx(i)}
                                                onDragOver={(e) => { e.preventDefault(); setPhotogOverIdx(i); }}
                                                onDragLeave={() => setPhotogOverIdx((v) => (v === i ? null : v))}
                                                onDrop={(e) => { e.preventDefault(); handlePhotogReorder(i); }}
                                                onDragEnd={() => { setPhotogDragIdx(null); setPhotogOverIdx(null); }}
                                            >
                                                <img
                                                    src={u}
                                                    alt={`Foto ${i + 1}`}
                                                    className="w-full h-full object-cover cursor-pointer hover:opacity-85 transition"
                                                    loading="lazy"
                                                    onClick={() => setPhotogLightboxIdx(i)}
                                                    data-testid={`photographer-open-photo-${i}`}
                                                />
                                                <div className="absolute top-1 right-1 flex flex-col items-end gap-1 pointer-events-none">
                                                    {photogCommentCounts[i] > 0 && (
                                                        <span
                                                            className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-[#E5A93D] text-black rounded-sm px-1.5 h-5"
                                                            data-testid={`photographer-photo-comment-badge-${i}`}
                                                            title={`${photogCommentCounts[i]} opmerking${photogCommentCounts[i] === 1 ? "" : "en"} op deze foto`}
                                                        >
                                                            <MessageSquare className="h-3 w-3" /> {photogCommentCounts[i]}
                                                        </span>
                                                    )}
                                                    {!!(req.ai_photo_previous_urls || {})[String(i)] && (
                                                        <span
                                                            className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-gradient-to-r from-[#E5A93D] to-[#F4C842] text-black rounded-sm px-1.5 h-5 shadow-[0_0_8px_rgba(229,169,61,0.5)]"
                                                            data-testid={`photographer-photo-ai-badge-${i}`}
                                                            title="Deze foto is AI-bewerkt"
                                                        >
                                                            <Sparkles className="h-3 w-3" /> AI
                                                        </span>
                                                    )}
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={(e) => { e.stopPropagation(); handlePhotogDelete(u); }}
                                                    className="absolute top-1 left-1 h-6 w-6 inline-flex items-center justify-center bg-black/70 hover:bg-red-600 text-white rounded-sm opacity-0 group-hover:opacity-100 transition"
                                                    title="Verwijder foto"
                                                    data-testid={`photographer-delete-photo-${i}`}
                                                >
                                                    <Trash2 className="h-3.5 w-3.5" />
                                                </button>
                                            </div>
                                        ))}
                                        </div>
                                    </>
                                )}
                                <PhotographerPhotoLightbox
                                    requestId={req.id}
                                    photos={req.ai_photo_urls || []}
                                    previousUrls={req.ai_photo_previous_urls || {}}
                                    openIndex={photogLightboxIdx}
                                    onClose={() => setPhotogLightboxIdx(null)}
                                    onChanged={refetchProject}
                                    addressLabel={req.address_street || ""}
                                    onEditWithAI={hasAnyTool ? (i) => { setPhotogLightboxIdx(null); setAiToolsIdx(i); setAiToolsOpen(true); } : null}
                                />
                            </div>
                        )}

                        {/* Products */}
                        {!isPhotographer && (
                        <div className="bg-[#FFFFFF] border border-zinc-100 rounded-md p-6 lg:p-8">
                            <h2 className="font-display font-bold text-xl tracking-tight mb-6">{t("pd.booked")}</h2>
                            <div className="space-y-3">
                                {req.items.map((it, idx) => {
                                    const p = products.find((x) => x.id === it.product_id);
                                    if (!p) return null;
                                    const photoCount = req.photo_urls?.length || 0;
                                    let basePrice = p.price;
                                    if (p.photo_tiers) {
                                        const tier = p.photo_tiers.find((t) => photoCount >= t.min_photos && photoCount <= t.max_photos);
                                        if (tier) basePrice = tier.price;
                                    }
                                    const selectedOptions = (it.option_ids || []).map((oid) => p.options?.find((o) => o.id === oid)).filter(Boolean);
                                    return (
                                        <React.Fragment key={idx}>
                                            <div className="flex items-start justify-between py-3 border-b border-zinc-100 last:border-0" data-testid={`booked-item-${it.product_id}`}>
                                                <div>
                                                    <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{p.name}</div>
                                                    <div className="font-medium text-zinc-950">{p.tagline}</div>
                                                </div>
                                                <div className="text-[#E5A93D] font-bold">€{basePrice.toFixed(2)}</div>
                                            </div>
                                            {selectedOptions.map((o) => (
                                                <div key={o.id} className="flex items-start justify-between py-2 pl-6 border-b border-zinc-100" data-testid={`booked-option-${o.id}`}>
                                                    <div className="text-sm text-zinc-600">+ {o.label}</div>
                                                    <div className="text-[#E5A93D] font-medium">€{Number(o.price).toFixed(2)}</div>
                                                </div>
                                            ))}
                                        </React.Fragment>
                                    );
                                })}
                                {(req.custom_items || []).map((ci, idx) => (
                                    <div key={ci.id || idx} className="flex items-start justify-between py-3 border-b border-zinc-100" data-testid={`custom-booked-item-${idx}`}>
                                        <div>
                                            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Extra</div>
                                            <div className="font-medium text-zinc-950">{ci.label}</div>
                                        </div>
                                        <div className="text-[#E5A93D] font-bold">€{Number(ci.price).toFixed(2)}</div>
                                    </div>
                                ))}
                                {(req.travel_cost > 0 || req.travel_distance_km > 0) && (
                                    <div className="flex items-start justify-between py-3 border-b border-zinc-100" data-testid="travel-line">
                                        <div>
                                            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{t("pd.travel")}</div>
                                            <div className="font-medium text-zinc-950">{req.travel_distance_km} km</div>
                                        </div>
                                        <div className="text-[#E5A93D] font-bold">{req.travel_cost > 0 ? `€${req.travel_cost.toFixed(2)}` : t("pd.included")}</div>
                                    </div>
                                )}
                                <div className="flex items-start justify-between py-3 pt-4">
                                    <div className="font-display font-bold text-lg">{t("pd.total")}</div>
                                    <div className="text-[#E5A93D] font-display font-black text-2xl">€{req.total_price}</div>
                                </div>
                            </div>
                        </div>
                        )}

                        {/* Beschrijving */}
                        {!isPhotographer && (
                        <div className="bg-[#FFFFFF] border border-zinc-100 rounded-md p-6 lg:p-8">
                            <div className="flex items-center justify-between mb-4 gap-3">
                                <div className="flex items-center gap-2">
                                    <FileText className="h-4 w-4 text-[#E5A93D]" />
                                    <h2 className="font-display font-bold text-xl tracking-tight">{t("pd.description")}</h2>
                                </div>
                                {!editingDescription && (
                                    <button
                                        onClick={startEditDescription}
                                        className="inline-flex items-center gap-1.5 text-xs text-zinc-500 hover:text-zinc-950 border border-zinc-200 rounded-sm h-8 px-3 hover:bg-zinc-100 transition"
                                        data-testid="edit-description-btn"
                                    >
                                        <Pencil className="h-3 w-3" /> {t("pd.edit")}
                                    </button>
                                )}
                            </div>
                            {editingDescription ? (
                                <div className="space-y-3" data-testid="edit-description-block">
                                    <textarea
                                        value={descriptionDraft}
                                        onChange={(e) => setDescriptionDraft(e.target.value)}
                                        rows={6}
                                        className="w-full bg-[#F4F4F5] border border-zinc-200 rounded-sm text-sm text-zinc-950 p-3 focus:outline-none focus:border-[#E5A93D]/40"
                                        placeholder={t("pd.desc_ph")}
                                        data-testid="description-textarea"
                                    />
                                    <div className="flex items-center gap-2">
                                        <Button
                                            onClick={saveDescription}
                                            disabled={savingDescription}
                                            className="bg-[#E5A93D] hover:bg-[#C8922C] text-black font-bold rounded-sm h-9 px-4"
                                            data-testid="description-save"
                                        >
                                            {savingDescription ? t("pd.saving") : t("pd.save")}
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            onClick={() => setEditingDescription(false)}
                                            disabled={savingDescription}
                                            className="text-zinc-500 hover:text-zinc-950 hover:bg-zinc-100 rounded-sm h-9 px-4"
                                            data-testid="description-cancel"
                                        >
                                            {t("pd.cancel")}
                                        </Button>
                                    </div>
                                </div>
                            ) : (
                                <p className="text-sm text-zinc-600 whitespace-pre-wrap leading-relaxed" data-testid="briefing-text">{req.description || <span className="text-zinc-500">{t("pd.no_desc")}</span>}</p>
                            )}
                            {req.property_link && (
                                <a href={req.property_link} target="_blank" rel="noopener noreferrer" className="mt-4 inline-flex items-center gap-2 text-sm text-[#E5A93D] hover:underline" data-testid="property-link-out">
                                    <LinkIcon className="h-3.5 w-3.5" /> {t("pd.view_sheet")}
                                </a>
                            )}
                            {req.photo_urls?.length > 0 && (
                                <div className="mt-6">
                                    <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
                                        <div className="text-xs uppercase tracking-wider text-zinc-500">{t("pd.supplied_photos")} ({req.photo_urls.length})</div>
                                        <button
                                            type="button"
                                            onClick={downloadAllPhotos}
                                            disabled={folderDl.state.active}
                                            className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-sm bg-zinc-100 hover:bg-zinc-100 border border-zinc-200 text-zinc-950 transition disabled:opacity-50"
                                            data-testid="download-all-photos-btn"
                                        >
                                            <Download className="h-3.5 w-3.5" />
                                            {t("pd.download_photos")}
                                        </button>
                                    </div>
                                    <PhotoGallery
                                        requestId={req.id}
                                        photos={req.photo_urls}
                                        field="photo_urls"
                                        canDelete={true}
                                        onDeleted={reload}
                                        testidPrefix="agent-photo"
                                    />
                                </div>
                            )}
                            {req.document_urls?.length > 0 && (
                                <div className="mt-6">
                                    <div className="text-xs uppercase tracking-wider text-zinc-500 mb-3">{t("pd.documents")} ({req.document_urls.length})</div>
                                    <div className="space-y-2">
                                        {req.document_urls.map((u) => {
                                            const url = typeof u === "string" ? u : u.url;
                                            const name = typeof u === "string" ? url.split("/").pop() : u.name;
                                            return (
                                                <button
                                                    type="button"
                                                    key={url}
                                                    onClick={() => downloadStatic(url, name)}
                                                    className="w-full text-left flex items-center gap-3 p-3 bg-[#F4F4F5] border border-zinc-200 hover:border-[#E5A93D]/50 rounded-sm"
                                                    data-testid={`doc-${url}`}
                                                >
                                                    <FileText className="h-4 w-4 text-[#E5A93D]" />
                                                    <span className="text-sm text-zinc-950 flex-1 truncate">{name}</span>
                                                    <Download className="h-3.5 w-3.5 text-[#E5A93D]" />
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                        )}

                        <AdditionalLinksPanel
                            requestId={req.id}
                            links={req.additional_links || []}
                            onChange={reload}
                            currentUser={user}
                        />

                        {!isPhotographer && (
                        <ScriptEditor
                            requestId={req.id}
                            initialScript={req.script}
                            initialBlocks={req.script_blocks}
                            updatedAt={req.script_updated_at}
                            updatedBy={req.script_updated_by}
                            items={req.items}
                            products={products}
                            onChange={(updated) => setReq(updated)}
                        />
                        )}
                    </div>

                    {/* Sidebar */}
                    <div className="space-y-4">
                        <InfoCard icon={Calendar} label={t("pd.planned")} hidden={isPhotographer}>
                            {req.scheduled_date ? (
                                <>
                                    <div className="text-zinc-950">{fmtDate(req.scheduled_date)}</div>
                                    <div className="inline-flex items-center gap-1 text-zinc-500 text-sm">
                                        <Clock className="h-3 w-3 text-[#E5A93D]" />
                                        {req.scheduled_time || "—"}
                                    </div>
                                </>
                            ) : (
                                <div className="space-y-2">
                                    <div className="text-[10px] text-zinc-400 uppercase tracking-wider mb-1">{t("pd.pref_moments")}</div>
                                    {[{ date: req.preferred_date, time: req.preferred_time }, ...(req.preferred_slots || [])]
                                        .filter((s) => s && s.date)
                                        .map((s, i) => (
                                            <div key={i} className="flex items-start gap-2" data-testid={`pref-slot-${i}`}>
                                                <span className="flex-shrink-0 inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold bg-[#E5A93D]/15 text-[#E5A93D] border border-[#E5A93D]/30">{i + 1}</span>
                                                <div>
                                                    <div className="text-zinc-950 text-sm">{fmtDate(s.date)}</div>
                                                    <div className="inline-flex items-center gap-1 text-zinc-500 text-xs">
                                                        <Clock className="h-3 w-3 text-[#E5A93D]" />
                                                        {s.time}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    {!req.preferred_date && (!req.preferred_slots || req.preferred_slots.length === 0) && (
                                        <div className="text-zinc-500 text-sm italic">{t("pd.date_tbd")}</div>
                                    )}
                                </div>
                            )}
                        </InfoCard>
                        <InfoCard icon={MapPin} label={t("pd.location")}>
                            <div className="text-zinc-950">{req.address_street}</div>
                            <div className="text-zinc-500 text-sm">{req.address_zip} {req.address_city}</div>
                        </InfoCard>
                        <InfoCard icon={Mail} label={t("pd.email")}>
                            <div className="text-zinc-950 text-sm">{req.email}</div>
                        </InfoCard>
                        <InfoCard icon={Phone} label={t("pd.phone")}>
                            <div className="text-zinc-950 text-sm">{req.phone}</div>
                        </InfoCard>
                        {req.admin_notes && (
                            <InfoCard icon={FileText} label={t("pd.sf_note")}>
                                <div className="text-sm text-zinc-600 whitespace-pre-wrap">{req.admin_notes}</div>
                            </InfoCard>
                        )}
                    </div>
                </div>
            </div>

            <ShareLinkDialog
                open={!!shareDialog}
                onOpenChange={(open) => { if (!open) setShareDialog(null); }}
                url={shareDialog?.url}
                title={shareDialog?.title || t("pd.share_video")}
                subject={shareDialog?.subject || t("pd.re_video")}
            />

            <EditRequestDetailsModal
                open={editDetailsOpen}
                onOpenChange={setEditDetailsOpen}
                req={req}
                onSaved={(updated) => setReq(updated)}
            />

            <ZipDownloadOverlay state={zipDl.state} onCancel={zipDl.cancel} />
            <FolderDownloadOverlay state={folderDl.state} onCancel={folderDl.cancel} />

            {downloadOffer && (
                <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setDownloadOffer(null)}>
                    <div className="bg-[#FFFFFF] border border-emerald-500/30 rounded-md max-w-md w-full p-6 lg:p-8" onClick={(e) => e.stopPropagation()} data-testid="approve-download-modal">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="h-12 w-12 rounded-full bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center">
                                <CheckCircle2 className="h-6 w-6 text-emerald-600" />
                            </div>
                            <div>
                                <h3 className="font-display font-bold text-2xl tracking-tight">{t("pd.approved")}</h3>
                                <p className="text-sm text-zinc-500">{t("pd.dl_now", { label: downloadOffer.label.toLowerCase() })}</p>
                            </div>
                        </div>
                        <p className="text-xs text-zinc-500 mb-6 leading-relaxed">
                            {t("pd.dl_later", { label: downloadOffer.label.toLowerCase() })}
                        </p>
                        <div className="flex items-center gap-2">
                            <Button
                                onClick={() => {
                                    downloadStatic(downloadOffer.url, downloadOffer.name);
                                    setDownloadOffer(null);
                                }}
                                className="bg-[#E5A93D] hover:bg-[#C8922C] text-black font-bold rounded-sm h-10 px-5 active:scale-[0.98]"
                                data-testid="approve-download-btn"
                            >
                                <Download className="h-4 w-4 mr-2" /> Download
                            </Button>
                            <Button
                                onClick={() => setDownloadOffer(null)}
                                variant="ghost"
                                className="text-zinc-500 hover:text-zinc-950"
                                data-testid="approve-download-skip-btn"
                            >
                                {t("pd.close")}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            <CreditPurchaseReturnWatcher />
            <AgentPhotoToolsModal
                open={aiToolsOpen}
                onClose={() => { setAiToolsOpen(false); setAiToolsIdx(null); }}
                requestId={req.id}
                photos={req.ai_photo_urls || []}
                initialIndex={aiToolsIdx}
                onRefetch={refetchProject}
            />
        </div>
    );
}

function InfoCard({ icon: Icon, label, children, hidden = false }) {
    if (hidden) return null;
    return (
        <div className="bg-[#FFFFFF] border border-zinc-100 rounded-md p-5">
            <div className="flex items-center gap-2 mb-2">
                <Icon className="h-3.5 w-3.5 text-[#E5A93D]" />
                <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{label}</span>
            </div>
            {children}
        </div>
    );
}

function RetentionInfo() {
    // Retentie uitgeschakeld: bestanden blijven beschikbaar tot het project verwijderd wordt.
    return null;
}

function _RetentionInfoLegacy({ deletionAt }) {
    if (!deletionAt) return null;
    const deleteDate = new Date(deletionAt);
    const days = Math.max(0, Math.ceil((deleteDate.getTime() - Date.now()) / 86400000));
    const urgent = days <= 2;
    return (
        <div
            className={`flex items-center gap-2 text-[11px] mb-2 px-3 py-1.5 rounded-sm border ${urgent ? "bg-[#EF4444]/10 border-[#EF4444]/30 text-[#EF4444]" : "bg-[#E5A93D]/10 border-[#E5A93D]/20 text-[#E5A93D]/90"}`}
            data-testid="retention-info"
        >
            <Trash2 className="h-3 w-3 flex-shrink-0" />
            <span>
                Wordt automatisch verwijderd op <span className="font-bold">{deleteDate.toLocaleDateString("nl-BE", { day: "2-digit", month: "long", year: "numeric" })}</span>
                {` (2 maanden na oplevering)`}
                {urgent && ` — nog ${days} dag${days === 1 ? "" : "en"}`}
            </span>
        </div>
    );
}

function VideoPlayer({ url }) {
    const BACKEND = process.env.REACT_APP_BACKEND_URL;
    const src = url.startsWith("http") ? url : `${BACKEND}${url}`;
    // Handle youtube/vimeo iframe embeds
    if (/youtube\.com|youtu\.be|vimeo\.com/.test(url)) {
        let embed = url;
        if (/youtu\.be\/([^?]+)/.test(url)) {
            const id = url.match(/youtu\.be\/([^?]+)/)[1];
            embed = `https://www.youtube.com/embed/${id}`;
        } else if (/youtube\.com\/watch\?v=([^&]+)/.test(url)) {
            const id = url.match(/v=([^&]+)/)[1];
            embed = `https://www.youtube.com/embed/${id}`;
        } else if (/vimeo\.com\/(\d+)/.test(url)) {
            const id = url.match(/vimeo\.com\/(\d+)/)[1];
            embed = `https://player.vimeo.com/video/${id}`;
        }
        return (
            <div className="aspect-video w-full bg-zinc-50 rounded-sm overflow-hidden border border-zinc-200">
                <iframe src={embed} className="w-full h-full" allowFullScreen title="video" />
            </div>
        );
    }
    return (
        <video controls src={src} className="w-full aspect-video bg-zinc-50 rounded-sm border border-zinc-200">
            Je browser ondersteunt dit video-element niet.
        </video>
    );
}

/**
 * Embeds the public PublicPreview gallery (`/preview/<token>`) inside the
 * makelaar's authenticated project view as an iframe. This gives them the
 * exact same lightbox + per-photo comments + drawing + slider + bulk download
 * UI as the share link, without duplicating that component. Comments posted
 * through the iframe go through the existing public-preview comment endpoint
 * which already triggers admin notifications.
 *
 * Auto-creates the share token on first view via
 * `GET /requests/{id}/photos-share-token` (owner or admin).
 */
function PhotosPreviewEmbed({ requestId, editable = false }) {
    const { t } = useTranslation();
    const [token, setToken] = useState(null);
    const [error, setError] = useState(null);
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get(`/requests/${requestId}/photos-share-token`);
                if (!cancelled) setToken(data.token);
            } catch (e) {
                if (!cancelled) setError(e.response?.data?.detail || t("pd.photos_fail"));
            }
        })();
        return () => { cancelled = true; };
    }, [requestId]);

    if (error) {
        return (
            <div className="p-6 text-sm text-red-700 bg-red-500/10 border border-red-500/20 rounded-sm" data-testid="photos-preview-error">
                {error}
            </div>
        );
    }
    if (!token) {
        return (
            <div className="p-6 text-sm text-zinc-500" data-testid="photos-preview-loading">
                {t("pd.photos_loading")}
            </div>
        );
    }
    return (
        <div className="relative w-full" style={{ height: "min(85vh, 1200px)" }} data-testid="photos-preview-embed">
            <iframe
                src={`/preview/${token}?embedded=1${editable ? "&edit=1" : ""}`}
                title="Fotoreeks preview"
                className="w-full h-full rounded-sm border border-zinc-200 bg-zinc-50"
                allow="fullscreen"
            />
        </div>
    );
}

